import palette from '@assets/theme/palette';
import { BitrixDisableUserGuard } from '@components/guards/bitrixUserGuard/BitrixUserGuard';
import { useShareContext } from '@context/useShareContext';
import { useMediaQuery } from '@material-ui/core';
import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip'; //
import useAdditionServicesContext from '@pages/AdditionalServices/containers/additionServicesContext';
import useWidgetTabsContext from '@pages/AdditionalServices/containers/widgetTabsContext';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ShareMenuProps {
  isShareNotAllowed: boolean;
}

function ShareMenu({ isShareNotAllowed }: ShareMenuProps) {
  const { state: shareState } = useShareContext();
  const { tabs: tabsState } = useWidgetTabsContext();
  const { state: serviceState } = useAdditionServicesContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['RoutesCalculationWidget']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const href = window.location.href;

  const shareButtonTooltipText = isShareNotAllowed
    ? t('RoutesCalculationWidget:ShareCalculationLinkNotAllowed')
    : t('RoutesCalculationWidget:ShareCalculationLink');

  const openMenuHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (tabsState.ActiveTabData.type === 'service' && !serviceState.IsNeedCalculateServices) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const closeMenuHandler = () => {
    setAnchorEl(null);
  };

  const getMessageText = async (isGetOnlyLink: boolean) => {
    let messageText = isGetOnlyLink
      ? href
      : `${t('RoutesCalculationWidget:ShareCalculationText')} ${encodeURIComponent(href)}`;

    if (serviceState.IsNeedCalculateServices) {
      messageText = isGetOnlyLink
        ? `${window.location.origin + window.location.pathname}?share=${shareState.ShareShortLink}`
        : `${t('RoutesCalculationWidget:ShareCalculationText')} ${
            window.location.origin + window.location.pathname
          }?share=${shareState.ShareShortLink}`;
    }

    return messageText;
  };

  const telegramShareHandler = async () => {
    closeMenuHandler();
    const href = await getMessageText(true);
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(href)}&text=${t(
        'RoutesCalculationWidget:ShareCalculationMailText'
      )}`
    );
  };

  const whatsAppShareHandler = async () => {
    closeMenuHandler();
    const messageText = await getMessageText(false);
    window.open(`https://wa.me/?text=${messageText}`);
  };

  const viberShareHandler = async () => {
    closeMenuHandler();
    const messageText = await getMessageText(false);
    window.open(`viber://forward?text=${messageText}`);
  };

  const emailShareHandler = async () => {
    closeMenuHandler();
    const href = await getMessageText(true);
    window.open(
      `mailto:?subject=${t('RoutesCalculationWidget:ShareCalculationText')}&body=${t(
        'RoutesCalculationWidget:ShareCalculationMailText'
      )}: ${encodeURIComponent(href)}`
    );
  };

  return (
    <>
      <BitrixDisableUserGuard>
        <Tooltip title={shareButtonTooltipText}>
          <IconButton
            color="primary"
            size="small"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={openMenuHandler}
          >
            <ShareIcon style={{ color: palette.light.grey[700], fontSize: "31px" }} />
          </IconButton>
        </Tooltip>
      </BitrixDisableUserGuard>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenuHandler}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={emailShareHandler}>Email</MenuItem>
        <MenuItem onClick={telegramShareHandler}>Telegram</MenuItem>
        {isMobile && <MenuItem onClick={viberShareHandler}>Viber</MenuItem>}
        <MenuItem onClick={whatsAppShareHandler}>WhatsApp</MenuItem>
      </Menu>
    </>
  );
}

export { ShareMenu };
